(function (root) {
  'use strict';

  const ROLES = ['moss', 'vessel', 'stones'];
  const LABELS = { moss: 'Moss', vessel: 'Vessel', stones: 'Stones' };
  const OPTION_NAMES = { moss: 'Moss balls', vessel: 'Vessel', stones: 'Stones' };
  const EXAMPLES = {
    moss: [
      { title: '1 moss ball', detail: 'A solo moment' },
      { title: '2 moss balls', detail: 'A little company' },
      { title: '3 moss balls', detail: 'The quiet trio' }
    ],
    vessel: [
      { title: 'Round', detail: 'The round one · soft curves' },
      { title: 'Tall', detail: 'The tall one · a little height' },
      { title: 'Wide', detail: 'The wide one · an open landscape' }
    ],
    stones: [
      { title: 'River light', detail: 'Warm, pale pebbles' },
      { title: 'River dark', detail: 'Deep, quiet stone' }
    ]
  };

  function safeImage(url) {
    return typeof url === 'string' && /^(https:\/\/|\/\/|\/(?!\/))/.test(url) ? url : null;
  }

  // Native products resolve by option names and values, never variant ordering.
  function normalizeProduct(raw, catalog) {
    catalog.mode = 'native';
    const product = raw && typeof raw === 'object' ? raw : {};
    const options = Array.isArray(product.options) ? product.options : [];
    const positions = ROLES.map(role => options.findIndex(option => option && option.name === OPTION_NAMES[role]));
    const schemaValid = options.length === 3 && positions.every(position => position >= 0) && ROLES.every((role, index) => {
      const values = options[positions[index]].values;
      return Array.isArray(values) && values.length === EXAMPLES[role].length &&
        EXAMPLES[role].every(example => values.includes(example.title));
    });
    const ids = new Set();
    const tuples = new Set();
    let malformed = !schemaValid;
    const variants = [];
    for (const item of Array.isArray(product.variants) ? product.variants : []) {
      const values = schemaValid && Array.isArray(item && item.options) && item.options.length === 3 ? positions.map(position => item.options[position]) : [];
      const tuple = JSON.stringify(values);
      if (!item || !/^\d+$/.test(String(item.id)) || ids.has(String(item.id)) || tuples.has(tuple) ||
          !Number.isSafeInteger(item.price) || item.price < 0 || values.length !== 3 ||
          !ROLES.every((role, index) => EXAMPLES[role].some(example => example.title === values[index]))) {
        malformed = true;
        continue;
      }
      ids.add(String(item.id));
      tuples.add(tuple);
      variants.push({
        id: String(item.id), values, price: item.price,
        available: item.available === true && product.requiresSellingPlan !== true &&
          (!item.minimum || item.minimum <= 1) && (!item.increment || item.increment <= 1) &&
          (item.maximum == null || item.maximum >= 1),
        image: safeImage(item.image), verifiedPhoto: item.verifiedPhoto === true
      });
    }
    catalog.product = {
      id: String(product.id || ''), title: typeof product.title === 'string' ? product.title : '',
      valid: !malformed && /^\d+$/.test(String(product.id)) && variants.length === 18,
      comingSoon: product.comingSoon === true,
      selectedVariantId: String(product.selectedVariantId || ''), variants
    };
    for (const role of ROLES) {
      catalog.groups[role] = EXAMPLES[role].map((example, index) => ({
        ...example, id: example.title, index, price: null, image: null, available: true,
        preview: false, concept: true
      }));
    }
    return catalog;
  }

  function variantTitle(item) {
    const product = typeof item.productTitle === 'string' ? item.productTitle.trim() : '';
    const variant = typeof item.variantTitle === 'string' ? item.variantTitle.trim() : '';
    return product + (variant && variant !== 'Default Title' ? ' — ' + variant : '');
  }

  function normalizeCatalog(raw) {
    const catalog = { mode: 'components', currency: 'USD', locale: 'en', cartUrl: '/cart', groups: {}, art: {} };
    if (raw && typeof raw.currency === 'string' && /^[A-Z]{3}$/.test(raw.currency)) catalog.currency = raw.currency;
    if (raw && typeof raw.locale === 'string') catalog.locale = raw.locale;
    if (raw && typeof raw.cartUrl === 'string' && /^\/(?!\/)/.test(raw.cartUrl)) catalog.cartUrl = raw.cartUrl;
    for (const key of ['moss', 'vessels', 'lightOne', 'lightTwo', 'lightThree', 'dark']) {
      const url = raw && raw.art && raw.art[key];
      catalog.art[key] = typeof url === 'string' && /^(https:\/\/|\/\/|\/(?!\/))/.test(url) ? url : null;
    }
    if (raw && raw.mode === 'native') return normalizeProduct(raw.product, catalog);
    for (const role of ROLES) {
      const input = raw && raw.groups && Array.isArray(raw.groups[role]) ? raw.groups[role] : [];
      const ids = new Set();
      const actual = input.filter(item => {
        if (!item || !/^\d+$/.test(String(item.id)) || ids.has(String(item.id))) return false;
        if (!Number.isSafeInteger(item.price) || item.price < 0 || !variantTitle(item)) return false;
        ids.add(String(item.id));
        return true;
      }).map((item, index) => ({
        id: String(item.id),
        title: variantTitle(item),
        price: item.price,
        available: item.available === true && item.requiresSellingPlan !== true &&
          (!item.minimum || item.minimum <= 1) && (!item.increment || item.increment <= 1),
        image: typeof item.image === 'string' && /^(https:\/\/|\/\/|\/(?!\/))/.test(item.image) ? item.image : null,
        detail: 'One item in your design',
        preview: false,
        index
      }));
      catalog.groups[role] = actual.length ? actual : EXAMPLES[role].map((item, index) => ({
        ...item, id: 'preview-' + role + '-' + index, price: null, available: true, preview: true, index, image: null
      }));
    }
    return catalog;
  }

  function initialSelection(catalog, variantId) {
    if (catalog.mode === 'native') {
      const variants = catalog.product.variants;
      const variant = variants.find(item => item.id === String(variantId || catalog.product.selectedVariantId)) ||
        variants.find(item => item.available && item.price > 0) || variants[0];
      return Object.fromEntries(ROLES.map((role, index) => [role, variant ? variant.values[index] : EXAMPLES[role][0].title]));
    }
    return Object.fromEntries(ROLES.map(role => [role, (catalog.groups[role].find(item => item.available) || {}).id || null]));
  }

  function selectedItems(catalog, selection) {
    return ROLES.map(role => ({ role, item: catalog.groups[role].find(item => item.id === selection[role]) || null }));
  }

  function designState(catalog, selection) {
    const selected = selectedItems(catalog, selection);
    if (catalog.mode === 'native') {
      const variant = catalog.product.variants.find(item => ROLES.every((role, index) => item.values[index] === selection[role])) || null;
      const isPreview = !catalog.product.valid || catalog.product.comingSoon;
      const hasPrice = !isPreview && variant && variant.price > 0;
      return {
        selected, variant, isPreview,
        canPurchase: Boolean(hasPrice && variant.available),
        total: hasPrice ? variant.price : null,
        configuration: selected.map(({ role, item }) => LABELS[role] + ': ' + (item ? item.title : 'Not selected')).join(' · ')
      };
    }
    const isPreview = ROLES.some(role => catalog.groups[role].some(item => item.preview));
    const canPurchase = selected.every(({ item }) => item && !item.preview && item.available);
    return {
      selected,
      isPreview,
      canPurchase,
      total: canPurchase ? selected.reduce((sum, entry) => sum + entry.item.price, 0) : null,
      configuration: selected.map(({ role, item }) => LABELS[role] + ': ' + (item ? item.title : 'Not selected')).join(' · ')
    };
  }

  // Clay's source sheets stay intact. These rectangles select a single scene,
  // excluding the white triptych dividers and the dark matrix's printed labels.
  function conceptFrame(catalog, key, crop) {
    const url = catalog.art[key];
    if (!url) return null;
    const size = key === 'dark' ? [1284, 1225] : [2172, 724];
    return { key, url, width: size[0], height: size[1], crop };
  }

  function triptychFrame(catalog, key, column) {
    if (!Number.isInteger(column) || column < 0 || column > 2) return null;
    return conceptFrame(catalog, key, [[8, 8, 708, 708], [732, 8, 708, 708], [1456, 8, 708, 708]][column]);
  }

  function darkFrame(catalog, count, vessel) {
    if (![count, vessel].every(value => Number.isInteger(value) && value >= 0 && value < 3)) return null;
    const columns = [[166, 358], [536, 352], [900, 376]];
    const rows = [[59, 351], [427, 327], [772, 340]];
    return conceptFrame(catalog, 'dark', [columns[vessel][0], rows[count][0], columns[vessel][1], rows[count][1]]);
  }

  function conceptChoice(catalog, role, index) {
    if (role === 'moss') return triptychFrame(catalog, 'moss', index);
    if (role === 'vessel') return triptychFrame(catalog, 'vessels', index);
    if (role === 'stones' && index === 0) return triptychFrame(catalog, 'lightOne', 0);
    if (role === 'stones' && index === 1) return darkFrame(catalog, 0, 0);
    return null;
  }

  function conceptScene(catalog, selection) {
    const selected = selectedItems(catalog, selection);
    // Collection order says nothing about moss quantity, vessel shape or stones.
    // Native option names have an explicit, validated mapping to the concepts.
    // Every such scene stays labelled as illustrative, not supplier photography.
    if (!selected.every(({ item }) => item && (item.preview || catalog.mode === 'native' && item.concept))) return null;
    const [moss, vessel, stones] = selected.map(({ item }) => item.index);
    if (![moss, vessel].every(value => Number.isInteger(value) && value >= 0 && value < 3)) return null;
    if (stones === 0) return triptychFrame(catalog, ['lightOne', 'lightTwo', 'lightThree'][moss], vessel);
    if (stones === 1) return darkFrame(catalog, moss, vessel);
    return null;
  }

  function buildCartItems(catalog, selection, kitId) {
    const state = designState(catalog, selection);
    if (!state.canPurchase) throw new Error('Choose an available product for every part of your design.');
    if (typeof kitId !== 'string' || !/^mm-[a-zA-Z0-9-]{10,100}$/.test(kitId)) throw new Error('A unique design reference is required.');
    if (catalog.mode === 'native') return [{ id: state.variant.id, quantity: 1, properties: { _mm_configuration: kitId } }];
    return state.selected.map(({ role, item }) => ({
      id: item.id,
      quantity: 1,
      properties: { _mm_kit: kitId, _mm_role: role, Configuration: state.configuration }
    }));
  }

  // Inspect only this add's unique group: a failed bulk add may still add stock.
  function verifyCartKit(cart, expected) {
    if (!cart || !Array.isArray(cart.items) || !expected.length) return { state: 'unknown' };
    const configurationId = expected[0].properties._mm_configuration;
    if (configurationId) {
      const matches = cart.items.filter(item => item.properties && item.properties._mm_configuration === configurationId);
      if (!matches.length) return { state: 'empty' };
      const complete = expected.length === 1 && matches.length === 1 &&
        String(matches[0].variant_id || matches[0].id) === String(expected[0].id) && matches[0].quantity === 1;
      return { state: complete ? 'complete' : 'partial' };
    }
    const kitId = expected[0].properties._mm_kit;
    const actual = cart.items.filter(item => item.properties && item.properties._mm_kit === kitId);
    if (!actual.length) return { state: 'empty' };
    const complete = actual.length === expected.length && expected.every(want => {
      const matches = actual.filter(item => String(item.variant_id || item.id) === String(want.id) && item.properties._mm_role === want.properties._mm_role);
      return matches.length === 1 && matches[0].quantity === want.quantity && matches[0].properties.Configuration === want.properties.Configuration;
    });
    return { state: complete ? 'complete' : 'partial' };
  }

  async function fetchWithDeadline(url, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      return await root.fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
  }

  const api = { normalizeCatalog, initialSelection, designState, buildCartItems, verifyCartKit, conceptChoice, conceptScene };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.document || !root.customElements || root.customElements.get('mm-builder')) return;

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function photoFrame(frame, description) {
    const node = element('span', 'mm-builder__photo');
    node.dataset.mmArtSource = frame.key;
    node.dataset.mmCrop = frame.crop.join(',');
    const [x, y, width, height] = frame.crop;
    node.style.aspectRatio = width + ' / ' + height;
    const img = element('img');
    img.src = frame.url;
    img.alt = description || '';
    img.width = frame.width;
    img.height = frame.height;
    img.decoding = 'async';
    img.style.width = (frame.width / width * 100) + '%';
    img.style.height = (frame.height / height * 100) + '%';
    img.style.left = (-x / width * 100) + '%';
    img.style.top = (-y / height * 100) + '%';
    node.append(img);
    return node;
  }


  class ModMossBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        if (this.onPopState) root.addEventListener('popstate', this.onPopState);
        if (this.onPageShow) root.addEventListener('pageshow', this.onPageShow);
        return;
      }
      const data = this.querySelector('[data-mm-catalog]');
      if (!data) return;
      this.initialized = true;
      try {
        this.catalog = normalizeCatalog(JSON.parse(data.textContent));
      } catch (_) {
        this.catalog = normalizeCatalog({});
      }
      this.selection = initialSelection(this.catalog, new URL(root.location.href).searchParams.get('variant'));
      this.currentStep = 'moss';
      this.busy = false;
      this.needsCartReview = false;
      this.addCompleted = false;
      this.artPrefix = 'mm-' + (this.dataset.sectionId || 'builder').replace(/[^a-zA-Z0-9-]/g, '-');
      try {
        this.money = new Intl.NumberFormat(this.catalog.locale, { style: 'currency', currency: this.catalog.currency });
      } catch (_) {
        this.money = new Intl.NumberFormat('en', { style: 'currency', currency: this.catalog.currency });
      }
      this.renderChoices();
      this.renderState();
      this.updateVariantUrl();
      this.addEventListener('change', event => {
        const input = event.target.closest('[data-mm-choice]');
        if (!input || this.busy || this.needsCartReview) return;
        this.selection[input.dataset.mmRole] = input.value;
        this.querySelector('[data-mm-status]').textContent = '';
        this.renderState();
        this.updateVariantUrl();
      });
      this.addEventListener('click', event => {
        const tab = event.target.closest('[data-mm-tab]');
        if (tab) this.setStep(tab.dataset.mmTab, false);
        if (event.target.closest('[data-mm-next]')) this.setStep(ROLES[(ROLES.indexOf(this.currentStep) + 1) % 3], true);
        if (event.target.closest('[data-mm-add]')) this.addToCart();
      });
      this.querySelector('[data-mm-tabs]').addEventListener('keydown', event => {
        const tab = event.target.closest('[data-mm-tab]');
        if (!tab) return;
        const index = ROLES.indexOf(tab.dataset.mmTab);
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % 3;
        if (event.key === 'ArrowLeft') next = (index + 2) % 3;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = 2;
        if (next === undefined) return;
        event.preventDefault();
        this.setStep(ROLES[next], true);
      });
      this.onPopState = () => {
        if (this.catalog.mode !== 'native' || this.busy || this.needsCartReview) return;
        this.selection = initialSelection(this.catalog, new URL(root.location.href).searchParams.get('variant'));
        this.renderState();
      };
      root.addEventListener('popstate', this.onPopState);
      this.onPageShow = event => {
        if (!event.persisted) return;
        // Back from the cart can restore this exact, disabled DOM from BFCache.
        // Only a confirmed receipt permits a new add; an interrupted write does not.
        if (this.addCompleted) {
          this.busy = false;
          this.addCompleted = false;
          this.needsCartReview = false;
          this.querySelector('[data-mm-status]').textContent = '';
        } else if (this.busy) {
          this.busy = false;
          this.needsCartReview = true;
          this.querySelector('[data-mm-status]').textContent = 'We couldn’t confirm whether your design reached the bag. Please check it before trying again.';
          this.querySelector('[data-mm-cart-link]').hidden = false;
        }
        this.renderState();
      };
      root.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() {
      if (this.onPopState) root.removeEventListener('popstate', this.onPopState);
      if (this.onPageShow) root.removeEventListener('pageshow', this.onPageShow);
    }

    updateVariantUrl() {
      if (this.catalog.mode !== 'native') return;
      const state = designState(this.catalog, this.selection);
      if (!this.catalog.product.valid || !state.variant) return;
      const url = new URL(root.location.href);
      url.searchParams.set('variant', state.variant.id);
      root.history.replaceState(root.history.state, '', url.href);
    }

    renderChoices() {
      for (const role of ROLES) {
        const container = this.querySelector('[data-mm-choices="' + role + '"]');
        container.replaceChildren();
        for (const choice of this.catalog.groups[role]) {
          const label = element('label', 'mm-builder__choice');
          const input = element('input');
          input.type = 'radio';
          input.name = this.artPrefix + '-' + role;
          input.value = choice.id;
          input.dataset.mmChoice = '';
          input.dataset.mmRole = role;
          input.checked = this.selection[role] === choice.id;
          input.disabled = !choice.available;
          const body = element('span', 'mm-builder__choice-body');
          const visual = element('span', 'mm-builder__choice-image');
          if (choice.image) {
            const img = element('img');
            img.src = choice.image;
            img.alt = '';
            img.loading = 'lazy';
            img.width = 240;
            img.height = 210;
            visual.append(img);
          } else {
            const art = (choice.preview || choice.concept) && conceptChoice(this.catalog, role, choice.index);
            if (art) visual.append(photoFrame(art, ''));
            else visual.append(element('span', 'mm-builder__no-photo', 'Image coming soon'));
          }
          const check = element('span', 'mm-builder__choice-check', '✓');
          check.setAttribute('aria-hidden', 'true');
          visual.append(check);
          const copy = element('span', 'mm-builder__choice-copy');
          copy.append(element('span', 'mm-builder__choice-title', choice.title));
          copy.append(element('span', 'mm-builder__choice-detail', choice.preview || choice.concept ? choice.detail : (choice.available ? 'One item in your design' : 'Currently unavailable')));
          copy.append(element('span', 'mm-builder__choice-price', this.catalog.mode === 'native' ? 'Configuration option' : choice.preview ? 'Preview option' : this.money.format(choice.price / 100)));
          body.append(visual, copy);
          label.append(input, body);
          container.append(label);
        }
      }
    }

    setStep(role, focus) {
      if (!ROLES.includes(role)) return;
      this.currentStep = role;
      for (const tab of this.querySelectorAll('[data-mm-tab]')) {
        const active = tab.dataset.mmTab === role;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      }
      for (const panel of this.querySelectorAll('[data-mm-panel]')) panel.hidden = panel.dataset.mmPanel !== role;
      const next = this.querySelector('[data-mm-next]');
      next.hidden = role === 'stones';
      next.replaceChildren(document.createTextNode(role === 'moss' ? 'Next: choose a vessel ' : 'Next: choose your stones '), element('span', '', '→'));
      next.lastChild.setAttribute('aria-hidden', 'true');
      this.querySelector('.mm-builder__scene-bottom span:last-child').textContent = '0' + (ROLES.indexOf(role) + 1) + ' / 03';
    }

    renderState() {
      const state = designState(this.catalog, this.selection);
      this.querySelector('[data-mm-availability]').hidden = !state.isPreview;
      this.querySelector('[data-mm-design-label]').textContent = state.isPreview ? 'An illustrative design' : this.catalog.mode === 'native' ? 'One terrarium. Made your way.' : 'Three pieces. One little world.';
      const summary = this.querySelector('[data-mm-summary]');
      summary.replaceChildren();
      for (const { role, item } of state.selected) {
        const row = element('div', 'mm-builder__summary-row');
        row.append(element('dt', '', LABELS[role]), element('dd', '', item ? item.title : 'Choose an available option'));
        summary.append(row);
      }
      this.renderScene(state);
      this.querySelector('[data-mm-total]').textContent = state.total !== null ? this.money.format(state.total / 100) : (state.isPreview ? 'Collection coming soon' : this.catalog.mode === 'native' ? 'Pricing coming soon' : 'Choose available options');
      this.querySelector('[data-mm-price-note]').hidden = state.total === null;
      const add = this.querySelector('[data-mm-add]');
      add.disabled = !state.canPurchase || this.busy || this.needsCartReview;
      const text = this.busy ? 'Adding your little world…' : this.needsCartReview ? 'Please check your bag' : state.canPurchase ? 'Add your little world to bag' : state.isPreview ? 'Collection coming soon' : this.catalog.mode === 'native' ? 'This configuration is unavailable' : 'Choose available options';
      const arrow = element('span', '', '↗');
      arrow.setAttribute('aria-hidden', 'true');
      add.replaceChildren(document.createTextNode(text + ' '), arrow);
      this.setAttribute('aria-busy', String(this.busy));
      for (const input of this.querySelectorAll('[data-mm-choice]')) {
        const choice = this.catalog.groups[input.dataset.mmRole].find(item => item.id === input.value);
        input.checked = this.selection[input.dataset.mmRole] === input.value;
        input.disabled = !choice.available || this.busy || this.needsCartReview;
      }
    }

    renderScene(state) {
      const container = this.querySelector('[data-mm-art]');
      const caption = this.querySelector('[data-mm-scene-caption]');
      const note = this.querySelector('[data-mm-illustration-note]');
      const scene = conceptScene(this.catalog, this.selection);
      container.replaceChildren();
      if (state.variant && state.variant.image) {
        container.classList.remove('mm-builder__art--pieces');
        const img = element('img');
        img.src = state.variant.image;
        img.alt = (state.variant.verifiedPhoto ? '' : 'Concept image: ') + state.configuration;
        img.width = 900;
        img.height = 900;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'contain';
        container.append(img);
        caption.textContent = state.configuration;
        note.textContent = state.variant.verifiedPhoto ? 'Product photograph. Living moss and natural stones vary in color and shape.' : 'Concept imagery, not supplier photography. Final products may vary in color, shape, and scale.';
        return;
      }
      container.classList.toggle('mm-builder__art--pieces', !scene);
      if (scene) {
        const [moss, vessel, stones] = state.selected.map(({ item }) => item);
        container.append(photoFrame(scene, 'Illustrative design: ' + moss.title + ', ' + vessel.title.toLowerCase() + ' glass vessel, ' + stones.title.toLowerCase() + ' stones.'));
        caption.textContent = ['A little green goes a long way.', 'Good things grow in good company.', 'Your very own quiet corner.'][moss.index];
        note.textContent = 'Concept imagery, not supplier photography. Final products may vary in color, shape, and scale.';
        return;
      }

      const pieces = element('ul', 'mm-builder__pieces');
      pieces.setAttribute('aria-label', 'Your selected components');
      for (const { role, item } of state.selected) {
        const piece = element('li', 'mm-builder__piece');
        const visual = element('span', 'mm-builder__piece-image');
        if (item && item.image) {
          const img = element('img');
          img.src = item.image;
          img.alt = '';
          img.width = 160;
          img.height = 160;
          visual.append(img);
        } else {
          const art = item && item.preview && conceptChoice(this.catalog, role, item.index);
          if (art) visual.append(photoFrame(art, ''));
          else visual.append(element('span', 'mm-builder__no-photo', 'Image coming soon'));
        }
        const copy = element('span', 'mm-builder__piece-copy');
        copy.append(element('span', 'mm-builder__piece-role', LABELS[role] + (item && item.preview ? ' · concept' : '')));
        copy.append(element('span', '', item ? item.title : 'Choose an available option'));
        piece.append(visual, copy);
        pieces.append(piece);
      }
      container.append(pieces);
      caption.textContent = 'Your selected pieces.';
      note.textContent = state.isPreview ? 'Concept options are illustrative. Product images show individual pieces, not the assembled design.' : 'Product images show individual pieces, not the assembled design. Check each listing for dimensions and details.';
    }

    async addToCart() {
      if (this.busy || this.needsCartReview || !designState(this.catalog, this.selection).canPurchase) return;
      const random = root.crypto && root.crypto.randomUUID ? root.crypto.randomUUID() : Array.from(root.crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('');
      const items = buildCartItems(this.catalog, this.selection, 'mm-' + random);
      const shopRoot = root.Shopify && root.Shopify.routes && /^\/(?!\/)/.test(root.Shopify.routes.root) ? root.Shopify.routes.root : '/';
      const base = shopRoot.endsWith('/') ? shopRoot : shopRoot + '/';
      const status = this.querySelector('[data-mm-status]');
      this.busy = true;
      status.textContent = '';
      this.renderState();
      let message = '';
      let writeMayHaveSucceeded = false;
      try {
        const response = await fetchWithDeadline(base + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        writeMayHaveSucceeded = response.ok || response.status >= 500;
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          if (typeof error.description === 'string') message = error.description;
        }
      } catch (_) {
        // Never retry a write after a transport error; inspect its unique group.
        writeMayHaveSucceeded = true;
      }

      let result = { state: 'unknown' };
      try {
        const response = await fetchWithDeadline(base + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (response.ok) result = verifyCartKit(await response.json(), items);
      } catch (_) { /* A bag review is required if the receipt cannot be read. */ }

      // An aborted write can still finish at Shopify after this read returned.
      if (result.state === 'empty' && writeMayHaveSucceeded) result = { state: 'unknown' };

      if (result.state === 'complete') {
        this.addCompleted = true;
        status.textContent = 'Your little world is in the bag.';
        root.location.assign(this.catalog.cartUrl);
        return;
      }
      this.busy = false;
      this.needsCartReview = result.state !== 'empty';
      this.querySelector('[data-mm-cart-link]').hidden = false;
      if (result.state === 'partial') {
        status.textContent = 'Only part of your design reached the bag. Please review those items there before adding another design.';
      } else if (result.state === 'unknown') {
        status.textContent = 'We couldn’t confirm whether your design reached the bag. Please check it before trying again.';
      } else {
        status.textContent = message || 'Your design could not be added. An item may have just sold out. Please check your choices and try again.';
      }
      this.renderState();
    }
  }

  root.customElements.define('mm-builder', ModMossBuilder);
})(typeof window !== 'undefined' ? window : globalThis);
